import React, { useEffect, useState } from 'react';
import { Button, Table, Pagination, message, Spin, Modal, Form, Input, Upload, InputNumber, Tooltip, Checkbox } from 'antd';
import { DownloadOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';

interface CommentsClusteringProps {
  username: string;
  isLoggedIn: boolean;
}

const CommentsClustering: React.FC<CommentsClusteringProps> = ({ username, isLoggedIn }) => {
  const [tasks, setTasks] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [form] = Form.useForm();

  const formatTime = (timeStr: string) => {
    if (!timeStr) return '-';
    const d = new Date(timeStr);
    return d.toLocaleString();
  };

  const fetchTasks = async (pageNum = 1, size = 10) => {
    if (!username) return;
    
    setLoading(true);
    try {
      const res = await fetch(`/comments_clustering/api/user_tasks?username=${encodeURIComponent(username)}&page=${pageNum}&page_size=${size}`);
      
      if (!res.ok) {
        console.error('fetchTasks: HTTP error:', res.status, res.statusText);
        message.error(`获取任务列表失败: ${res.status} ${res.statusText}`);
        return;
      }
      
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.error('fetchTasks: Invalid content type:', contentType);
        const text = await res.text();
        console.error('fetchTasks: Response text:', text);
        message.error('获取任务列表失败: 服务器返回格式错误');
        return;
      }
      
      const data = await res.json();
      setTasks(data.tasks || []);
      setTotal(data.total || 0);
      setPage(data.page || 1);
      setPageSize(data.page_size || 10);
    } catch (e) {
      console.error('fetchTasks: error:', e);
      message.error('获取任务列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (p: number, ps: number) => {
    fetchTasks(p, ps);
  };

  const handleDownload = (task_id: string) => {
    if (!username) {
      message.error('用户未登录');
      return;
    }
    window.open(`/comments_clustering/api/download/${task_id}`);
  };

  const handleModalOk = async () => {
    if (!username) {
      message.error('用户未登录');
      return;
    }
    
    try {
      const values = await form.validateFields();
      const fileList = values.file;
      if (!fileList || !fileList.length || !fileList[0].originFileObj) {
        message.error('请上传文件');
        return;
      }
      setSubmitLoading(true);
      const formData = new FormData();
      formData.append('file', fileList[0].originFileObj);
      formData.append('username', username);
      if (values.event_background) formData.append('event_background', values.event_background);
      if (values.max_clusters) formData.append('max_clusters', values.max_clusters);
      if (values.task_name) formData.append('task_name', values.task_name);
      if (values.text_column) formData.append('text_column', values.text_column);
      if (values.enable_sentiment !== undefined) formData.append('enable_sentiment', values.enable_sentiment.toString());
      
      const res = await fetch('/comments_clustering/api/submit_task', {
        method: 'POST',
        body: formData,
      });
      let data: any = {};
      try {
        data = await res.json();
      } catch (e) {
        message.error('服务端返回格式错误');
        return;
      }
      if (data.task_id) {
        message.success('任务提交成功！');
        setModalOpen(false);
        form.resetFields();
        fetchTasks(page, pageSize);
      } else {
        message.error('任务提交失败');
      }
    } catch (e) {
      message.error('网络异常或表单校验失败');
    } finally {
      setSubmitLoading(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn && username) {
      fetchTasks(page, pageSize);
    }
  }, [isLoggedIn, username]);

  const columns = [
    {
      title: '任务ID',
      dataIndex: 'task_id',
      key: 'task_id',
      width: 120,
      render: (text: string) => (
        <Tooltip title={text} placement="topLeft">
          <span>{text?.slice(0, 10)}{text && text.length > 10 ? '...' : ''}</span>
        </Tooltip>
      )
    },
    {
      title: '任务名',
      dataIndex: 'task_name',
      key: 'task_name',
      width: 120,
      render: (text: string) => (
        <Tooltip title={text} placement="topLeft">
          <span>{text?.slice(0, 10)}{text && text.length > 10 ? '...' : ''}</span>
        </Tooltip>
      )
    },
    {
      title: '事件背景',
      dataIndex: 'event_background',
      key: 'event_background',
      width: 240,
      render: (text: string) => (
        <Tooltip title={text} placement="topLeft">
          <span>{text?.slice(0, 30)}{text && text.length > 30 ? '...' : ''}</span>
        </Tooltip>
      )
    },
    { 
      title: '状态', 
      dataIndex: 'status', 
      key: 'status', 
      width: 100, 
      render: (text: string, record: any) => {
        let color = '#999';
        if (text === 'finished') color = '#52c41a';
        if (text === 'pending') color = '#1890ff';
        if (text === 'failed') color = '#ff4d4f';
        const map: { [key: string]: string } = { finished: '已完成', pending: '进行中', failed: '失败' };
        
        if (text === 'failed' && record.error) {
          return (
            <Tooltip title={`失败原因：${record.error}`} placement="topLeft">
              <span style={{ color, cursor: 'help' }}>{map[text] || text}</span>
            </Tooltip>
          );
        }
        
        return <span style={{ color }}>{map[text] || text}</span>;
      } 
    },
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 160, render: formatTime },
    { title: '完成时间', dataIndex: 'finished_at', key: 'finished_at', width: 160, render: formatTime },
    { title: '最大聚类数', dataIndex: 'max_clusters', key: 'max_clusters', width: 100 },
    { title: '评论列名', dataIndex: 'text_column', key: 'text_column', width: 120 },
    { 
      title: '情感分类', 
      dataIndex: 'enable_sentiment', 
      key: 'enable_sentiment', 
      width: 100,
      render: (value: boolean) => (
        <span style={{ color: value ? '#52c41a' : '#999' }}>
          {value ? '是' : '否'}
        </span>
      )
    },
    { 
      title: '操作', 
      key: 'action', 
      width: 100, 
      render: (_: any, record: any) => (
        record.status === 'finished' ?
          <Button icon={<DownloadOutlined />} size="small" onClick={() => handleDownload(record.task_id)}>下载</Button>
          : <span style={{ color: '#ccc' }}>-</span>
      ) 
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
          新增评论观点聚类任务
        </Button>
        <Button onClick={() => fetchTasks(page, pageSize)}>刷新数据</Button>
      </div>
      <Spin spinning={loading} tip="加载中...">
        <Table
          columns={columns}
          dataSource={tasks}
          pagination={false}
          bordered
          rowKey="task_id"
          scroll={{ x: 1300 }}
          size="middle"
        />
      </Spin>
      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
        <Pagination
          current={page}
          total={total}
          pageSize={pageSize}
          showSizeChanger
          pageSizeOptions={["10", "30", "50"]}
          onChange={handlePageChange}
          onShowSizeChange={handlePageChange}
        />
      </div>

      <Modal
        title="新建评论观点聚类任务"
        open={modalOpen}
        onOk={handleModalOk}
        onCancel={() => setModalOpen(false)}
        confirmLoading={submitLoading}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ max_clusters: 11 }}
        >
          <Form.Item
            name="file"
            label={<span>上传文件 <span style={{ color: '#999', fontSize: 12, marginLeft: 4 }}>支持excel或者csv文件格式</span></span>}
            valuePropName="fileList"
            getValueFromEvent={e => (Array.isArray(e) ? e : e && e.fileList)}
            rules={[{ required: true, message: '请上传CSV或Excel文件' }]}
          >
            <Upload beforeUpload={() => false} maxCount={1} accept=".csv,.xls,.xlsx">
              <Button icon={<UploadOutlined />}>选择文件</Button>
            </Upload>
          </Form.Item>
          <Form.Item name="event_background" label="事件背景">
            <Input.TextArea rows={3} placeholder="可选，事件背景信息，建议填写" />
          </Form.Item>
          <Form.Item
            name="max_clusters"
            label={<span>最大聚类数 <span style={{ color: '#999', fontSize: 12, marginLeft: 4 }}>请输入5～25之间的整数</span></span>}
            rules={[{ required: true, message: '请输入最大聚类数' }, { type: 'number', min: 5, max: 25, message: '最大聚类数需为5~25之间的整数' }]}
          >
            <InputNumber min={5} max={25} style={{ width: '100%' }} placeholder="5~25之间的整数" />
          </Form.Item>
          <Form.Item name="task_name" label="任务名称">
            <Input placeholder="可选，未填写则自动生成" />
          </Form.Item>
          <Form.Item name="text_column" label="评论文本列名">
            <Input placeholder="可选，评论文本所在列名，未填写则默认为'评论'或者'文本'" />
          </Form.Item>
          <Form.Item 
            name="enable_sentiment" 
            label="是否进行情感分类"
            valuePropName="checked"
            initialValue={false}
          >
            <Checkbox>
              <span style={{ color: '#999', fontSize: 12 }}>
                勾选该字段会增加成本和任务完成时间，非必要不勾选
              </span>
            </Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CommentsClustering; 